import { DESTAQUES, FONTES_DE_ETIQUETA, MODELOS_DE_ETIQUETA, ROTULO_DO_CAMPO, TAMANHOS_DE_ETIQUETA } from "@bookdental/schemas";
import type { EtiquetaDeEntrega, EtiquetaDeProducao } from "@bookdental/schemas";
import { Checkbox } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { ListaDeCampos } from "./ListaDeCampos";
import { OpcaoQrDoCatalogo } from "./OpcaoQrDoCatalogo";

const NOME_DO_TAMANHO: Record<string, string> = { "60x40": "60 × 40 mm", "50x30": "50 × 30 mm", "100x50": "100 × 50 mm", a4: "Folha A4 (3 × 7)" };
const NOME_DA_FONTE: Record<string, string> = { compacta: "Compacta", normal: "Normal", grande: "Grande" };
const NOME_DO_DESTAQUE: Record<string, string> = { ...ROTULO_DO_CAMPO, codigo: "Código do serviço" };

const selectCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground";

type Config = EtiquetaDeProducao | EtiquetaDeEntrega;

/** Modelo pronto, tamanho, fonte, destaque, logo e a lista de campos de uma etiqueta. */
export function ControlesDaEtiqueta<T extends Config>({
  tipo,
  config,
  onChange,
}: {
  tipo: "producao" | "entrega";
  config: T;
  onChange: (config: T) => void;
}) {
  const muda = (parcial: Partial<T>) => onChange({ ...config, ...parcial });
  const modelos = MODELOS_DE_ETIQUETA.filter((m) => m.tipo === tipo);

  return (
    <div className="space-y-4">
      <div>
        <p className={labelCls}>Começar de um modelo</p>
        <div className="flex flex-wrap gap-1.5">
          {modelos.map((m) => (
            <button
              key={m.chave}
              type="button"
              onClick={() => onChange(m.config as T)}
              title={m.descricao}
              className="inline-flex h-8 items-center rounded-md border border-zinc-200 bg-white px-2.5 text-[12px] font-semibold text-zinc-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-border dark:bg-card dark:text-foreground"
            >
              {m.nome}
            </button>
          ))}
        </div>
      </div>

      <div className={cn("grid gap-3", tipo === "producao" ? "sm:grid-cols-3" : "sm:grid-cols-2")}>
        <label>
          <span className={labelCls}>Tamanho</span>
          <select value={config.tamanho} onChange={(e) => muda({ tamanho: e.target.value } as Partial<T>)} className={selectCls}>
            {TAMANHOS_DE_ETIQUETA.map((t) => (
              <option key={t} value={t}>{NOME_DO_TAMANHO[t]}</option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Letra</span>
          <select value={config.fonte} onChange={(e) => muda({ fonte: e.target.value } as Partial<T>)} className={selectCls}>
            {FONTES_DE_ETIQUETA.map((f) => (
              <option key={f} value={f}>{NOME_DA_FONTE[f]}</option>
            ))}
          </select>
        </label>
        {"destaque" in config && (
          <label>
            <span className={labelCls}>Em destaque</span>
            <select value={config.destaque} onChange={(e) => muda({ destaque: e.target.value } as unknown as Partial<T>)} className={selectCls}>
              {DESTAQUES.map((d) => (
                <option key={d} value={d}>{NOME_DO_DESTAQUE[d]}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-foreground">
        <Checkbox checked={config.logo} onCheckedChange={(v) => muda({ logo: v === true } as Partial<T>)} />
        Logo do laboratório na etiqueta
      </label>
      {tipo === "entrega" && (
        <OpcaoQrDoCatalogo
          checked={(config as unknown as { qrCatalogo: boolean }).qrCatalogo}
          onChange={(qrCatalogo) => muda({ qrCatalogo } as unknown as Partial<T>)}
          onde="na etiqueta de entrega"
        />
      )}

      <div>
        <p className={labelCls}>Campos — arraste para mudar a ordem</p>
        <ListaDeCampos campos={config.campos} onChange={(campos) => muda({ campos } as Partial<T>)} />
        <p className="mt-1.5 text-[11px] text-zinc-500 dark:text-muted-foreground">
          {tipo === "producao"
            ? "O código do serviço (1042-1) vai sempre. Cor, prazo, dentes, data, etapa e técnico vão na coluna da direita."
            : "Destinatário e endereço vão sempre."}
        </p>
      </div>
    </div>
  );
}
